import React,{useState, useEffect, useRef} from 'react'
import LogoImage from "../../assets/img/logo.svg";
import axios from '../../plugins/axios';
import Toast from "../../plugins/useToast";
import { ReactComponent as IconTextdel } from '../../assets/icons/ic-text-del.svg'
import { useTranslation } from "react-i18next";

const initialPatientInfo = {
    displayDevicePatientId: true,
    devicePatientId: '',
    displayName: true,
    name: '',
    birthDate: '',
    gender: 'M',
    category: 'ADULT',
    height: '',
    weight: '',
    phoneNumber: '',
    address: '',
    protectorName: '',
    protectorPhoneNumber: '',
    room: '',
    doctor1: {
        doctorName: '',
        doctorPhoneNumber: ''
    },
    doctor2: {
        doctorName: '',
        doctorPhoneNumber: ''
    },
    nurse1: {
        nurseName: '',
        nursePhoneNumber: ''
    },
    nurse2: {
        nurseName: '',
        nursePhoneNumber: ''
    },
    nurse3: {
        nurseName: '',
        nursePhoneNumber: ''
    }
};

const Index = () => {
    const { t } = useTranslation()
    const [barcodeSettings, setBarcodeSettings] = useState({
        "format" : "",
        "length" : 1
    })

    const [barcode, setBarcode] = useState('')
    const [wardInfo, setWardInfo] = useState('')
    const [deviceInfo, setDeviceInfo] = useState('')
    const [patientInfo, setPatientInfo] = useState('')
    const [scanType, setScanType] = useState('Device') //Device, patient
    const [errorMsg, setErrorMsg] = useState('')
    const [scanBuffer, setScanBuffer] = useState(''); // Barcode Scanning을 위한 useState
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Ward 정보 가져오기
    const getWardInfo = async () => {
        try{
            const res = await axios.get('/wards');
            if(res.code != '200') throw res;
            setWardInfo(res.data.content); 
        }
        catch(err){
            console.warn(err);
        }
    }

    // Admin에서 세팅한 Barcode 세팅 값 가져오기
    const getBarcodeSettings = async () => {
        try{
            const res = await axios.get('/system/barcode/settings');
            setBarcodeSettings(res.data);            
        }
        catch(err){
            console.warn(err);
        }
    }

    // Device 정보 가져오기
    const getDeviceInfo = async (barcodeValue) => {
        try{
            const res = await axios.get(`/device/serial/${barcodeValue}`);
            if(res.code != '200') throw res;

            setDeviceInfo(res.data);
            setScanType('Patient');            

            if(res.data.patientId){
                getPatientInfo(res.data.patientId);
            }else{
                setPatientInfo('');
            }            
        }
        catch(err){
            setScanType('Device');
            setErrorMsg(err.message);
            setDeviceInfo('');
            setPatientInfo('');
            console.warn(err);
        }
    }

    // Patient 정보 가져오기
    const getPatientInfo = async (patientId) => {
        try{
            const res = await axios.get(`/patient/${patientId}`);
            if(res.code != '200') throw res;

            setPatientInfo(res.data);
        }
        catch(err){
            console.warn(err);
        }
    }
    
    // handleBarcodeScan에서 API 호출
    const handleBarcodeScan = (e) => {
        if (document.activeElement === inputRef.current) return;

        if (e.key === 'Enter') {
            handleBarcode(scanBuffer);
            setScanBuffer('');            
        } else if (e.key.length === 1) {
            setScanBuffer(prev => prev + e.key);
        }
    };

    // Input에서 Enter 눌렀을 때
    const handleInputEnter = (e) => {
        if (e.key === 'Enter') {
            handleBarcode(barcode);
        }
    };

    // Barcode 로직
    const handleBarcode = async (barcodeValue) => {
        try{
            console.log('스캔된 바코드:', barcodeValue);
            setBarcode('');            

            // 바코드 길이 체크 후 API 호출
            if (barcodeValue) {
                if (scanType === 'Device') {                    
                    getDeviceInfo(barcodeValue);
                } else if (scanType === 'Patient') {
                    if(!deviceInfo.patientId){
                        // Patient Length 체크
                        if(barcodeSettings.length != barcodeValue.length){
                            Toast({ isSuccess: false, msg: 'After scanning the device barcode, please scan the patient or discharge barcode' });
                            return;
                        }

                        // Patient Format 체크
                        if(barcodeSettings.format == 'NUMBER'){
                            if(!/^\d+$/.test(barcodeValue)){
                                Toast({ isSuccess: false, msg: 'After scanning the device barcode, please scan the patient or discharge barcode' });
                                return;
                            }
                        }else if(barcodeSettings.format == 'NUMBER_AND_STRING'){                            
                            if(!/^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z0-9]+$/.test(barcodeValue)){
                                Toast({ isSuccess: false, msg: 'After scanning the device barcode, please scan the patient or discharge barcode' });
                                return;
                            }                               
                        }

                        const payload = {
                            ...initialPatientInfo
                        }

                        payload.devicePatientId = barcodeValue;
                        payload.name = 'Unknown';  
                        payload.deviceId = deviceInfo.id;                      

                        const resAdmit =  await axios.post('/patient', payload);
                        if (resAdmit.code != '200') throw resAdmit;

                        Toast({ isSuccess: true, msg: 'Admission completed.' });
                        initData();
                    }else{
                        //퇴원
                        if(barcodeValue.toUpperCase() == 'DISCHARGE'){
                            const payload = {
                                status: "UNASSIGNED"
                            }

                            const resDischarge = await axios.patch(`/patient/${deviceInfo.patientId}/status`,payload);
                            if (resDischarge.code != '200') throw resDischarge

                            //Status 변경              
                            Toast({ isSuccess: true, msg: 'Discharge completed.' });
                            initData();
                        }else{
                            const resDevice = await axios.get(`/device/serial/${barcodeValue}`);
                            if(resDevice.code != '200') throw resDevice;

                            if(resDevice.data.patientId){
                                Toast({ isSuccess: false, msg: 'The device is already admitted to another patient.' });
                                return;
                            }else{
                                const payload = {
                                    deviceId : resDevice.data.id,
                                }

                                const res = await axios.patch(`/patient/${deviceInfo.patientId}/transfer/device`,payload);
                                if (res.code != '200') throw res;

                                Toast({ isSuccess: true, msg: 'Transfer completed.' });
                                initData();
                            }
                        }
                    }
                }
            }
        }
        catch(err){
            Toast({ isSuccess: false, msg: err.message });
            console.warn(err);
        }
        
    }

    const initData = () => {
        setBarcode('');
        setScanType('Device');
        setDeviceInfo('');
        setPatientInfo('');
    }

    useEffect(() => {
        containerRef.current && containerRef.current.focus();
        getBarcodeSettings();
        getWardInfo();
    }, [])    

    //Barcode 값이 세팅되면 Type에 따라서 Device, Patient 정보 가져오기
    useEffect(() => {
        setErrorMsg('');
    }, [barcode])

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 flex items-center justify-center z-[9999]" 
            ref={containerRef}
            tabIndex={0}
            onKeyDown={handleBarcodeScan}
        >
            <div className="fixed inset-0 bg-[#113577] flex flex-col items-center justify-center z-50"
                style={{
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                }}
            >   
                <div
                    className="w-[492px] h-[60px] mb-[40px]"
                    style={{
                        backgroundImage: `url(${LogoImage})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        backgroundRepeat: "no-repeat",
                    }}
                />
                <div className='w-[540px] flex flex-col justify-start items-center gap-[24px] bg-[#0D2756] rounded-[8px] shadow-[inset_0_1px_0_0_rgba(0,0,0,0.4)] p-[20px]'>
                    <p className='text-[#E1E4E5] text-center font-pretendard text-[24px] font-medium leading-[120%]'>{t('barcode.title')}</p>
                    <div className="w-full">
                        <div className="mt-2" style={{ backgroundColor: 'white', padding: '14px 23px', borderRadius: 8, border: '1px solid #CECECE' }}>
                            <div className="flex relative items-center">
                                <input ref={inputRef} className='' type='text' value={barcode} onKeyDown={(e) => handleInputEnter(e)} onChange={(e) => setBarcode(e.target.value)}  style={{ backgroundColor: 'transparent', borderColor: '#c3c3c3', border : 'none', outline: 'none', width: '100%', paddingRight: 25 }}/>
                                {scanType == 'Patient' && <IconTextdel className='absolute right-0 cursor-pointer' onClick={() => { setScanType('Device'); setBarcode(''); }} />}
                            </div>                        
                        </div>
                        {
                            errorMsg && <p className='mt-[8px] text-[#FF002E] font-pretendard text-[16px] font-normal leading-[18px]'>{t('barcode.invalidDevice')}</p>
                        }
                    </div>
                    {
                        scanType == 'Patient' && (
                            <div className='w-full flex justify-between items-start mt-[4px] pt-[24px] border-t border-[#1F1F1F]'>                        
                                <div className='flex-1 flex flex-col flex-wrap'>
                                    <p className='text-white text-[24px] font-medium mb-[8px]'>{t('barcode.deviceInfo')}</p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.serial')} : <span>{deviceInfo.serial}</span></p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.ward')} : <span>{wardInfo.filter((ward) => ward.id == deviceInfo?.wardId).map((ward) => ward.name).join(', ')}</span></p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.bedNumber')} : <span>{deviceInfo?.bedNumber}</span></p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.status')} : <span>{deviceInfo?.patientId ? t('barcode.admitted') : t('barcode.notAdmitted')}</span></p>
                                </div>
                                <div className='flex-1 flex flex-col flex-wrap whitespace-nowrap'>
                                    <p className='text-white text-[24px] font-medium mb-[8px]'>{t('barcode.patientInfo')}</p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.name')} : <span>{patientInfo?.name || '-'}</span></p>
                                    <p className='text-white text-[20px] font-light'>- {t('barcode.patientId')} : <span>{patientInfo?.devicePatientId || '-'}</span></p>
                                </div>
                            </div>
                        )
                    }
                </div>                
            </div>
        </div>
    )
}

export default Index